import type { AccountInfo } from '@azure/msal-browser';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import type { BDError } from '~/common/models/error.model';

/**
 * Datadog filters these logs support:
 *   @feature:login                               - every login log
 *   @feature:login @outcome:service-unavailable  - reads that sent a user to /service-unavailable
 *   @feature:login @hasNoProfile:true            - reads that failed because the user has not enrolled yet
 *   @feature:login @errorCode:FETCH_ERROR        - transport failures that carry no HTTP status
 *   @feature:login @errorCodes:<code>            - failures the API explained with an error code
 *   @traceId:<id>                                - a failed read, to match against backend traces
 *   @login.isNewUser:true                        - any log from a first-time sign-in
 */
const LOGIN_FEATURE = 'login';
const LOGIN_CONTEXT_KEY = 'login';
/** Set by `InboundRedirect` through the telemetry provider once the profile loads. */
const USER_CONTEXT_KEY = 'user';

export const LOGIN_SERVICE_UNAVAILABLE_ACTION = 'login_service_unavailable';

/** Which of the two reads that make up the signed-in user failed. */
export const LoginRead = {
  PROFILE: 'profile',
  PERMISSIONS: 'permissions',
} as const;
export type LoginRead = (typeof LoginRead)[keyof typeof LoginRead];

/** Where a failed read sends the user from the login bootstrap. */
export const LoginReadOutcome = {
  ENROLLMENT: 'enrollment',
  LOGOUT: 'logout',
  SERVICE_UNAVAILABLE: 'service-unavailable',
} as const;
export type LoginReadOutcome =
  (typeof LoginReadOutcome)[keyof typeof LoginReadOutcome];

/**
 * A first-time user has no profile, and so no permissions, until they enroll
 * their business and accept terms: both reads failing then is the normal first
 * login. A 401 is an expired session that login recovers from by signing the
 * user out.
 */
const getReadFailureLevel = (
  status: number | undefined,
  hasNoProfile: boolean
): 'info' | 'warn' | 'error' => {
  if (hasNoProfile) {
    return 'info';
  }
  return status === 401 ? 'warn' : 'error';
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const pickStrings = (entries: unknown[], key: string): string[] | undefined => {
  const values = entries
    .map((entry) => (isRecord(entry) ? entry[key] : undefined))
    .filter((value): value is string => typeof value === 'string');
  return values.length > 0 ? values : undefined;
};

/**
 * Pulls the identifying fields off the API's error envelope
 * (`GMResponseCommon`) in the RTK Query error a `BDError` wraps. The body is
 * never logged whole: `errors[].message` is free text that can echo user input,
 * `result` is untyped, and a gateway error (the 401s and 404s the specs leave
 * undocumented) may not be this envelope at all, in which case nothing is kept.
 */
const describeResponseBody = (error: BDError): Record<string, unknown> => {
  const body = isRecord(error.data) ? error.data.data : undefined;
  if (!isRecord(body)) {
    return {};
  }

  const errors = Array.isArray(body.errors) ? body.errors : [];
  return {
    traceId: typeof body.traceId === 'string' ? body.traceId : undefined,
    errorCodes: pickStrings(errors, 'errorCode'),
    errorTypes: pickStrings(errors, 'errorType'),
    unauthorizedRequest:
      typeof body.unauthorizedRequest === 'boolean'
        ? body.unauthorizedRequest
        : undefined,
  };
};

/**
 * Describes a failed read by status and codes only. Its message is left out: an
 * exception's text can carry backend detail or user input, and a parse error's
 * can quote the response body.
 */
const describeReadError = (error: BDError): Record<string, unknown> => {
  const originalStatus = isRecord(error.data)
    ? error.data.originalStatus
    : undefined;
  return {
    httpStatus: error.status,
    errorCode: error.code,
    // A `PARSING_ERROR` (e.g. a gateway's HTML error page) hides the real status here.
    originalHttpStatus:
      typeof originalStatus === 'number' ? originalStatus : undefined,
    ...describeResponseBody(error),
  };
};

/**
 * Logs a failed profile or permissions read, wherever in the app it happened.
 * @param read - Which read failed
 * @param error - The error as mirrored into the profile slice
 * @param hasNoProfile - Whether the profile read found no profile for the user
 */
export const logUserReadFailure = (
  read: LoginRead,
  error: BDError,
  hasNoProfile: boolean
): void => {
  datadogLogs.logger[getReadFailureLevel(error.status, hasNoProfile)](
    `User ${read} read failed`,
    {
      feature: LOGIN_FEATURE,
      read,
      hasNoProfile,
      ...describeReadError(error),
    }
  );
};

/**
 * Logs where the login bootstrap routes a user after one of their reads failed,
 * and records a RUM action when that is the service-unavailable page.
 * @param read - Which read failed
 * @param error - The error the decision was made on
 * @param outcome - Where the user is being sent
 */
export const logLoginReadDecision = ({
  read,
  error,
  outcome,
}: {
  read: LoginRead;
  error: BDError;
  outcome: LoginReadOutcome;
}): void => {
  const context = {
    feature: LOGIN_FEATURE,
    read,
    outcome,
    ...describeReadError(error),
  };
  const isServiceUnavailable = outcome === LoginReadOutcome.SERVICE_UNAVAILABLE;

  datadogLogs.logger[isServiceUnavailable ? 'error' : 'info'](
    `Login routed to ${outcome} after ${read} read failed`,
    context
  );
  if (isServiceUnavailable) {
    datadogRum.addAction(LOGIN_SERVICE_UNAVAILABLE_ACTION, context);
  }
};

/**
 * Attaches the identity of the signed-in MSAL account to every Datadog log and
 * RUM event as `login.*`, so logs from users who never get a profile can still
 * be traced. Carries only opaque IDs and enumerated claims, never names or
 * emails.
 * @param account - The active (or first cached) MSAL account
 * @param authType - The stored auth type (CDI or SSO), if any
 * @param cachedAccountCount - How many accounts MSAL holds
 */
export const setLoginAccountContext = (
  account: AccountInfo,
  authType: string | null | undefined,
  cachedAccountCount: number
): void => {
  const claims = account.idTokenClaims;
  const context = {
    accountId: account.localAccountId,
    homeAccountId: account.homeAccountId,
    tenantId: account.tenantId,
    environment: account.environment,
    authType,
    cachedAccountCount,
    isNewUser: claims?.newUser,
    idp: claims?.idp,
    policy: claims?.tfp ?? claims?.acr,
  };
  datadogLogs.setGlobalContextProperty(LOGIN_CONTEXT_KEY, context);
  datadogRum.setGlobalContextProperty(LOGIN_CONTEXT_KEY, context);
};

/**
 * Removes the signed-in user from Datadog: the `login.*` account identity and
 * the profile-based `user.*` context login adds once the profile loads, so logs
 * after sign-out are not attributed to the previous user.
 */
export const clearUserTelemetryContext = (): void => {
  for (const key of [LOGIN_CONTEXT_KEY, USER_CONTEXT_KEY]) {
    datadogLogs.removeGlobalContextProperty(key);
    datadogRum.removeGlobalContextProperty(key);
  }
};
